import React, { useState, useEffect } from 'react';
import {
  collection, onSnapshot, doc, updateDoc, setDoc, query, orderBy
} from 'firebase/firestore';
import { sendPasswordResetEmail } from 'firebase/auth';
import { db, auth } from './firebase';
import { SUITE_APPS, getAllActiveAppIds } from './constants/appCatalog';
import './AdminPanel.css';

const ROLES = ['Admin', 'Tester/QA', 'Customer'];

function AdminPanel({ user, onBack }) {
  const [users, setUsers]       = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [saving, setSaving]     = useState({});
  const [editingApps, setEditingApps] = useState(null);
  // Build 25: separate modal state for editing per-user suite-app access.
  const [editingSuiteApps, setEditingSuiteApps] = useState(null);

  useEffect(() => {
    const q = query(collection(db, 'users'), orderBy('email'));
    const unsub = onSnapshot(q, snap => {
      setUsers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const q = query(collection(db, 'applications'), orderBy('name'));
    const unsub = onSnapshot(q, snap => {
      setApplications(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, []);

  const handleRoleChange = async (userId, newRole) => {
    setSaving(prev => ({ ...prev, [userId]: true }));
    try {
      await updateDoc(doc(db, 'users', userId), { role: newRole });
    } catch (err) {
      alert('Error updating role. Please try again.');
    } finally {
      setSaving(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleToggleDisabled = async (u) => {
    setSaving(prev => ({ ...prev, [u.id]: true }));
    try {
      await updateDoc(doc(db, 'users', u.id), { disabled: !u.disabled });
    } catch (err) {
      alert('Error updating account status.');
    } finally {
      setSaving(prev => ({ ...prev, [u.id]: false }));
    }
  };

  const handleResetPassword = async (u) => {
    if (!window.confirm(`Send password reset email to ${u.email}?`)) return;
    setSaving(prev => ({ ...prev, [u.id]: true }));
    try {
      await sendPasswordResetEmail(auth, u.email);
      alert(`Password reset email sent to ${u.email}`);
    } catch (err) {
      alert('Error sending password reset email.');
    } finally {
      setSaving(prev => ({ ...prev, [u.id]: false }));
    }
  };

  const handleEditApps = (u) => {
    setEditingApps({
      userId: u.id,
      selectedApps: u.assignedApps || []
    });
  };

  const handleToggleApp = (appName) => {
    setEditingApps(prev => {
      const selected = prev.selectedApps.includes(appName)
        ? prev.selectedApps.filter(a => a !== appName)
        : [...prev.selectedApps, appName];
      return { ...prev, selectedApps: selected };
    });
  };

  const handleSaveApps = async () => {
    if (!editingApps) return;
    setSaving(prev => ({ ...prev, [editingApps.userId]: true }));
    try {
      await updateDoc(doc(db, 'users', editingApps.userId), {
        assignedApps: editingApps.selectedApps
      });
      setEditingApps(null);
    } catch (err) {
      alert('Error updating app assignments.');
    } finally {
      setSaving(prev => ({ ...prev, [editingApps.userId]: false }));
    }
  };

  // ── Build 25: per-user suite-app access ─────────────────────────────────
  // These manage which top-level suite apps (Defect Tracker, Notes, SOP, etc)
  // a user sees on their dashboard. Distinct from `assignedApps` above which
  // is for product applications a customer can file defects against.

  const handleEditSuiteApps = (u) => {
    setEditingSuiteApps({
      userId: u.id,
      userEmail: u.email,
      // Build 25.1: admins are no longer bypassed at runtime; their
      // assignedSuiteApps actually controls what they see. They can use
      // "Reset mine" on their own row for one-click recovery.
      selectedAppIds: u.assignedSuiteApps || []
    });
  };

  const handleToggleSuiteApp = (appId) => {
    setEditingSuiteApps(prev => {
      const selected = prev.selectedAppIds.includes(appId)
        ? prev.selectedAppIds.filter(a => a !== appId)
        : [...prev.selectedAppIds, appId];
      return { ...prev, selectedAppIds: selected };
    });
  };

  const handleSelectAllSuiteApps = () => {
    setEditingSuiteApps(prev => ({
      ...prev,
      selectedAppIds: SUITE_APPS.map(a => a.id)
    }));
  };

  const handleClearAllSuiteApps = () => {
    setEditingSuiteApps(prev => ({ ...prev, selectedAppIds: [] }));
  };

  const handleSaveSuiteApps = async () => {
    if (!editingSuiteApps) return;
    const userId = editingSuiteApps.userId;
    setSaving(prev => ({ ...prev, [userId]: true }));
    try {
      await updateDoc(doc(db, 'users', userId), {
        assignedSuiteApps: editingSuiteApps.selectedAppIds
      });
      setEditingSuiteApps(null);
    } catch (err) {
      console.error('Error updating suite-app access:', err);
      alert(`Error updating suite-app access: ${err?.code || err?.message || 'Unknown error'}`);
    } finally {
      setSaving(prev => ({ ...prev, [userId]: false }));
    }
  };

  // Build 25.1: "Reset mine" — restore the current admin's access to all
  // active suite apps. Quick recovery from over-restricting yourself.
  // Org-level enabled list is unaffected; this only changes the user's
  // personal assignedSuiteApps.
  const handleResetMyApps = async (u) => {
    if (u.id !== user.uid) return; // safety: only operate on self
    if (!window.confirm('Restore your own access to all currently active suite apps?')) {
      return;
    }
    setSaving(prev => ({ ...prev, [u.id]: true }));
    try {
      await updateDoc(doc(db, 'users', u.id), {
        assignedSuiteApps: getAllActiveAppIds()
      });
    } catch (err) {
      console.error('Error resetting own apps:', err);
      alert(`Error: ${err?.code || err?.message || 'Unknown error'}`);
    } finally {
      setSaving(prev => ({ ...prev, [u.id]: false }));
    }
  };

  const filteredUsers = users.filter(u =>
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  const getRoleBadgeClass = (role) => {
    if (role === 'Admin')      return 'role-badge role-badge--admin';
    if (role === 'Tester/QA')  return 'role-badge role-badge--tester';
    if (role === 'Customer')   return 'role-badge role-badge--customer';
    return 'role-badge role-badge--none';
  };

  return (
    <div className="admin-panel">
      <header className="header">
        <div className="header-content">
          <div>
            <h1>Wixon & Cross LLC</h1>
            <h2>User Management</h2>
          </div>
          <div className="header-user">
            <span className="user-email">{user.email}</span>
            <span className="role-badge role-badge--admin">Admin</span>
            <button className="btn btn-secondary btn-small" onClick={onBack}>
              ← Back to Defects
            </button>
          </div>
        </div>
      </header>

      <main className="main-content">
        <div className="admin-toolbar">
          <h3>Registered Users ({users.length})</h3>
          <input
            type="text"
            className="search-input"
            placeholder="Search by email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        <div className="admin-stats">
          {ROLES.map(role => (
            <div key={role} className="stat-card">
              <div className="stat-value">{users.filter(u => u.role === role).length}</div>
              <div className="stat-label">{role}</div>
            </div>
          ))}
          <div className="stat-card stat-card--alert">
            <div className="stat-value">{users.filter(u => !u.role).length}</div>
            <div className="stat-label">Unassigned</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{users.filter(u => u.disabled).length}</div>
            <div className="stat-label">Disabled</div>
          </div>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading users...</p>
          </div>
        ) : (
          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Current Role</th>
                  <th>Assign Role</th>
                  <th>Product Apps</th>
                  <th>Suite Access</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="empty-cell">No users found.</td>
                  </tr>
                ) : (
                  filteredUsers.map(u => (
                    <tr key={u.id} className={u.disabled ? 'row-disabled' : ''}>
                      <td className="email-cell">
                        {u.email}
                        {u.id === user.uid && <span className="you-tag"> (you)</span>}
                      </td>
                      <td>
                        <span className={getRoleBadgeClass(u.role)}>
                          {u.role || 'Unassigned'}
                        </span>
                      </td>
                      <td>
                        <select
                          value={u.role || ''}
                          onChange={e => handleRoleChange(u.id, e.target.value)}
                          disabled={saving[u.id] || u.disabled}
                          className="role-select"
                        >
                          <option value="">-- Select Role --</option>
                          {ROLES.map(r => (
                            <option key={r} value={r}>{r}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <div className="assigned-apps-cell">
                          {u.assignedApps && u.assignedApps.length > 0 ? (
                            <span className="apps-count">{u.assignedApps.length} app{u.assignedApps.length > 1 ? 's' : ''}</span>
                          ) : (
                            <span className="apps-none">None</span>
                          )}
                          <button
                            className="btn btn-small"
                            onClick={() => handleEditApps(u)}
                            disabled={saving[u.id]}
                          >
                            Edit
                          </button>
                        </div>
                      </td>
                      <td>
                        <div className="assigned-apps-cell">
                          {u.assignedSuiteApps && u.assignedSuiteApps.length > 0 ? (
                            <span className="apps-count">{u.assignedSuiteApps.length} app{u.assignedSuiteApps.length > 1 ? 's' : ''}</span>
                          ) : u.assignedSuiteApps && u.assignedSuiteApps.length === 0 ? (
                            <span className="apps-none">No access</span>
                          ) : (
                            <span className="apps-none">Not set</span>
                          )}
                          <button
                            className="btn btn-small"
                            onClick={() => handleEditSuiteApps(u)}
                            disabled={saving[u.id]}
                          >
                            Edit
                          </button>
                          {u.id === user.uid && (
                            <button
                              className="btn btn-small btn-secondary"
                              onClick={() => handleResetMyApps(u)}
                              disabled={saving[u.id]}
                              title="Restore your own access to all active apps"
                            >
                              Reset mine
                            </button>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className={`status-pill ${u.disabled ? 'status-pill--disabled' : 'status-pill--active'}`}>
                          {u.disabled ? 'Disabled' : 'Active'}
                        </span>
                      </td>
                      <td>
                        {u.id !== user.uid && (
                          <>
                            <button
                              className="btn btn-small btn-warning"
                              onClick={() => handleResetPassword(u)}
                              disabled={saving[u.id]}
                              style={{marginBottom: '0.25rem', width: '100%'}}
                            >
                              Reset Password
                            </button>
                            <button
                              className={`btn btn-small ${u.disabled ? 'btn-enable' : 'btn-disable'}`}
                              onClick={() => handleToggleDisabled(u)}
                              disabled={saving[u.id]}
                              style={{width: '100%'}}
                            >
                              {saving[u.id] ? '...' : (u.disabled ? 'Enable' : 'Disable')}
                            </button>
                          </>
                        )}
                        {u.id === user.uid && (
                          <span className="no-action">—</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        <div className="admin-note">
          <strong>Note:</strong> New users who sign up will appear here without a role assigned.
          Assign them a role before they can access the defect tracker.
          Disabled users cannot log in but their data is preserved.
        </div>

        {/* App Assignment Modal */}
        {editingApps && (
          <div className="modal-overlay" onClick={() => setEditingApps(null)}>
            <div className="modal modal-small" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Assign Product Applications</h3>
                <button className="close-btn" onClick={() => setEditingApps(null)}>×</button>
              </div>
              <p className="modal-help-text">
                Controls which product applications this user (Customer role) can file defects against.
              </p>
              <div className="apps-checklist">
                {applications.map(app => (
                  <label key={app.id} className="app-checkbox-label">
                    <input
                      type="checkbox"
                      checked={editingApps.selectedApps.includes(app.name)}
                      onChange={() => handleToggleApp(app.name)}
                    />
                    <span>{app.name}</span>
                  </label>
                ))}
                {applications.length === 0 && (
                  <p className="no-apps-note">No applications defined yet. Create apps in Application Manager first.</p>
                )}
              </div>
              <div className="modal-actions">
                <button className="btn btn-secondary" onClick={() => setEditingApps(null)}>
                  Cancel
                </button>
                <button 
                  className="btn btn-primary" 
                  onClick={handleSaveApps}
                  disabled={saving[editingApps.userId]}
                >
                  {saving[editingApps.userId] ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Build 25: Suite App Access Modal */}
        {editingSuiteApps && (
          <div className="modal-overlay" onClick={() => setEditingSuiteApps(null)}>
            <div className="modal modal-medium" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Suite App Access — {editingSuiteApps.userEmail}</h3>
                <button className="close-btn" onClick={() => setEditingSuiteApps(null)}>×</button>
              </div>
              <p className="modal-help-text">
                Controls which top-level suite apps this user sees on their dashboard. Unchecked apps are hidden from the dashboard, Desktop menus, and route URLs. /admin itself remains accessible to admins regardless of these settings.
              </p>
              <div className="suite-apps-actions">
                <button
                  type="button"
                  className="btn btn-small btn-secondary"
                  onClick={handleSelectAllSuiteApps}
                >
                  Select All
                </button>
                <button
                  type="button"
                  className="btn btn-small btn-secondary"
                  onClick={handleClearAllSuiteApps}
                >
                  Clear All
                </button>
                <span className="suite-apps-count-display">
                  {editingSuiteApps.selectedAppIds.length} of {SUITE_APPS.length} selected
                </span>
              </div>
              <div className="apps-checklist suite-apps-checklist">
                {SUITE_APPS.map(app => (
                  <label key={app.id} className="app-checkbox-label">
                    <input
                      type="checkbox"
                      checked={editingSuiteApps.selectedAppIds.includes(app.id)}
                      onChange={() => handleToggleSuiteApp(app.id)}
                    />
                    <span className="suite-app-row">
                      <span className="suite-app-icon">{app.icon}</span>
                      <span className="suite-app-name">{app.name}</span>
                      <span className="suite-app-desc">{app.description}</span>
                    </span>
                  </label>
                ))}
              </div>
              <div className="modal-actions">
                <button className="btn btn-secondary" onClick={() => setEditingSuiteApps(null)}>
                  Cancel
                </button>
                <button 
                  className="btn btn-primary" 
                  onClick={handleSaveSuiteApps}
                  disabled={saving[editingSuiteApps.userId]}
                >
                  {saving[editingSuiteApps.userId] ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      <footer className="footer">
        <p>&copy; 2026 Wixon & Cross LLC. All rights reserved.</p>
      </footer>
    </div>
  );
}

export default AdminPanel;
